/**
 * Navigation grouping, extracted as a pure function.
 *
 * The rail needs its items bucketed by group and its buckets in a fixed order.
 * Doing that inline would make the logic reachable only by rendering the shell
 * with a mocked auth context at several roles; as a function it is tested
 * directly, which is both faster and a better test.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.5.5, 6.5.10
 */
import { NAV_GROUPS, type NavGroup, type NavItem } from './nav-items';

/**
 * Record counts shown beside the rail's links, keyed by `NavItem.countKey`.
 *
 * Every key is optional: counts arrive from separate queries that resolve at
 * different times, and a partial object is the normal state rather than an
 * error one.
 */
export type NavCounts = Partial<Record<NonNullable<NavItem['countKey']>, number>>;

/** Items bucketed by group. Every group is present, possibly empty. */
export type GroupedNav = Record<NavGroup, NavItem[]>;

/**
 * Bucket items by their group, preserving each group's item order.
 *
 * Every group key exists in the result even when empty, so a caller can index
 * it without a presence check — the rail then filters empties out for display.
 * That is deliberate: a role-filtered menu can legitimately empty a whole
 * group (a viewer sees no Matchmaker and no Admin, emptying "Insight" of
 * everything but Analytics), and a missing key would crash rather than render
 * nothing.
 */
export function groupNavItems(items: readonly NavItem[]): GroupedNav {
  const grouped = Object.fromEntries(
    NAV_GROUPS.map((group) => [group, [] as NavItem[]]),
  ) as GroupedNav;

  for (const item of items) {
    grouped[item.group].push(item);
  }

  return grouped;
}
